import type { FlowDocument } from "./types";

/** The lowest-numbered "e_<n>" not already used — matches the shipped
 * examples' own "e1"/"e2" convention closely enough while guaranteeing
 * uniqueness without scanning their exact numbering scheme. */
export function nextEdgeId(doc: FlowDocument): string {
  const used = new Set(doc.edges.map((edge) => edge.id));
  let n = 1;
  let id = `e_${n}`;
  while (used.has(id)) {
    n += 1;
    id = `e_${n}`;
  }
  return id;
}

/**
 * Appends one new edge with a caller-given port name (FlowCanvas's own
 * onConnect handler defaults new connections to "next", editable
 * immediately afterward in the Inspector).
 *
 * Still deliberately does not reject a duplicate source+port or a port name
 * outside the source node's own allowed set at append time — every new
 * connection starts with the same default port ("next"), regardless of the
 * source node's type, and is renamed afterward in the Inspector; rejecting
 * here would make it impossible to draw a first edge from any node type
 * whose allowed ports don't include "next" (amd_gate, gather, branch,
 * repeat, transfer), or a second edge from a node that already has one.
 * Both rules ARE now mirrored (docs/plans/06's "Semantic validation —
 * implement in Go; mirror diagnostics in web"), as diagnostics instead:
 * `checkDuplicatePort`/`checkUnboundedLoop` (cycles.ts) and
 * `checkUnknownPorts` (requiredPorts.ts), composed into the same
 * diagnostics panel as the schema and missing-port checks (Inspector.tsx).
 */
export function addEdge(doc: FlowDocument, id: string, source: string, port: string, target: string): FlowDocument {
  return {
    ...doc,
    edges: [...doc.edges, { id, source, port, target }],
  };
}

/** Replaces one edge's own port name — the pure update Inspector's edge
 * editor hands to onChange, the same shape setNodeConfig already
 * establishes for a node's config. */
export function setEdgePort(doc: FlowDocument, edgeId: string, port: string): FlowDocument {
  return {
    ...doc,
    edges: doc.edges.map((edge) => (edge.id === edgeId ? { ...edge, port } : edge)),
  };
}

/** Removes one edge. Unlike removeNode's start-node guard, no edge is
 * ever structurally required to exist, so this never refuses. */
export function removeEdge(doc: FlowDocument, edgeId: string): FlowDocument {
  return {
    ...doc,
    edges: doc.edges.filter((edge) => edge.id !== edgeId),
  };
}
